import { useState, useMemo, useEffect } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Loader2 } from "lucide-react";

import { AccountingPreview } from "@/components/shared/AccountingPreview";
import { LoadingPage } from "@/components/shared/LoadingPage";
import { PageHeader } from "@/components/shared/PageHeader";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { formatDisplayDate, todayInUruguay } from "@/lib/dateFormat";
import {
  invoiceCreationBlockedEmptyExplanationKey,
  useInvoiceCreationBlocked,
} from "@/hooks/useInvoiceCreationBlocked";
import { invoiceKeys } from "@/lib/queryKeys";
import {
  createDebitNote,
  getInvoice,
  type CreateDebitNoteRequest,
  type InvoiceDetailEnriched,
} from "@/services/invoiceService";

const VAT_RATE = 0.22;

function buildDebitPayload(
  invoice: InvoiceDetailEnriched,
  totalAmount: number,
  description: string,
  issueDate: string
): CreateDebitNoteRequest {
  const currency = (invoice.currency ?? "UYU") as "UYU" | "USD";
  const subtotal = Math.round((totalAmount / (1 + VAT_RATE)) * 100) / 100;
  const taxAmount = Math.round((totalAmount - subtotal) * 100) / 100;
  return {
    currency,
    issueDate,
    receiverSnapshot: invoice.receiverSnapshot ?? { name: "", rut: "" },
    lines: [
      {
        lineNumber: 1,
        description: description || "Nota de débito",
        quantity: 1,
        unitPrice: { currency, amount: subtotal },
        tax: { category: "vat_basic" as const, rate: VAT_RATE, amount: taxAmount },
        lineTotal: { currency, amount: totalAmount },
      },
    ],
    totals: {
      subtotal: { currency, amount: subtotal },
      taxTotal: { currency, amount: taxAmount },
      total: { currency, amount: totalAmount },
    },
  };
}

export function DebitNoteWizardPage(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { t, i18n } = useTranslation();

  const [step, setStep] = useState(1);
  const [debitAmount, setDebitAmount] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [confirmed, setConfirmed] = useState(false);

  const { blocked: invoicingBlocked, reason: invoicingBlockedReason } =
    useInvoiceCreationBlocked();

  useEffect(() => {
    if (!id || !invoicingBlocked) return;
    navigate(`/invoices/${id}`, { replace: true });
    const key = invoiceCreationBlockedEmptyExplanationKey(invoicingBlockedReason);
    if (key) toast(t(key), { id: "invoicing-blocked" });
  }, [id, invoicingBlocked, invoicingBlockedReason, navigate, t]);

  const { data: invoice, isLoading, isError } = useQuery({
    queryKey: invoiceKeys.detail(id ?? ""),
    queryFn: () => getInvoice(id!),
    enabled: Boolean(id),
  });

  const createMutation = useMutation({
    mutationFn: (payload: CreateDebitNoteRequest) =>
      createDebitNote(id!, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: invoiceKeys.detail(id ?? "") });
      toast.success(t("invoices.notes.debit.created", "Debit Note created. It will be sent to DGI."));
      navigate(`/invoices/${id}`);
    },
    onError: (err: { response?: { data?: { message?: string }; status?: number } }) => {
      const msg =
        err?.response?.data?.message ??
        (err as Error).message ??
        t("invoices.notes.debit.error", "Failed to create Debit Note.");
      toast.error(msg);
    },
  });

  const remainingBalance =
    typeof invoice?.remainingBalance === "number" ? invoice.remainingBalance : 0;
  const totalCreditedSoFar = typeof invoice?.totalCredited === "number" ? invoice.totalCredited : 0;
  const originalTotal =
    typeof invoice?.originalInvoiceTotal === "number" ? invoice.originalInvoiceTotal : 0;

  const amountNum = useMemo(() => {
    const n = parseFloat(debitAmount.replace(",", "."));
    return Number.isFinite(n) && n >= 0 ? n : 0;
  }, [debitAmount]);

  const newRemaining = remainingBalance + amountNum;
  const amountError =
    amountNum <= 0 && step >= 2
      ? t("invoices.notes.debit.amountRequired", "Enter a valid amount.")
      : null;

  const canProceedStep2 = amountNum > 0;
  const canSubmitStep4 = confirmed;

  if (!id) {
    return (
      <div className="fx-content">
        <PageHeader title={t("invoices.notes.debit.title", "Issue Debit Note")} />
        <p className="text-sm text-muted-foreground">{t("invoices.detail.invalidId", "Invalid invoice")}</p>
      </div>
    );
  }

  if (invoicingBlocked) {
    return <LoadingPage />;
  }

  if (isLoading || !invoice) {
    return <LoadingPage />;
  }

  if (isError || invoice.status !== "sent" || (invoice as unknown as Record<string, unknown>).dgiStatus !== "confirmed") {
    return (
      <div className="fx-content">
        <PageHeader title={t("invoices.notes.debit.title", "Issue Debit Note")} onBack={() => navigate(`/invoices/${id}`)} />
        <p className="text-sm text-muted-foreground">
          {t("invoices.notes.debit.invalidContext", "This invoice cannot receive a Debit Note.")}
        </p>
      </div>
    );
  }

  const cfNumber = String(invoice.internalNumber ?? "");
  const issueDate = todayInUruguay();

  const handleSubmit = (): void => {
    const payload = buildDebitPayload(
      invoice,
      amountNum,
      reason.trim() || "Nota de débito",
      issueDate
    );
    createMutation.mutate(payload);
  };

  return (
    <div className="space-y-4 fx-content">
      <PageHeader
        title={t("invoices.notes.debit.title", "Issue Debit Note")}
        description={t("invoices.notes.debit.stepOf", "Step {{current}} of 4", { current: step })}
        onBack={
          step === 1
            ? () => navigate(`/invoices/${id}`)
            : () => setStep((s) => s - 1)
        }
      />

      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-foreground">
              {t("invoices.notes.debit.step1Title", "Context")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t("invoices.notes.debit.step1Explanation", "A Debit Note increases the amount owed on this invoice. Currency must match the original invoice.")}
            </p>
            <dl className="grid gap-2 text-sm">
              <div>
                <dt className="text-muted-foreground">{t("invoices.detail.relatedDocuments.cfeNumber", "CFE number")}</dt>
                <dd className="font-medium">{cfNumber}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t("invoices.detail.issueDate", "Issue date")}</dt>
                <dd className="font-medium">
                  {invoice.issueDate
                    ? formatDisplayDate(invoice.issueDate, {
                        locale: i18n.language,
                        useCalendarDate: true,
                      })
                    : "—"}
                </dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t("invoices.detail.receiver", "Receiver")}</dt>
                <dd className="font-medium">{invoice.receiverSnapshot?.name ?? "—"}</dd>
              </div>
              <div>
                <dt className="text-muted-foreground">{t("invoices.detail.financialSummary.remainingBalance", "Remaining balance")}</dt>
                <dd className="font-semibold text-foreground">
                  {invoice.currency ?? "UYU"} {remainingBalance.toLocaleString("es-UY", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </dd>
              </div>
            </dl>
            <div className="flex justify-end">
              <Button onClick={() => setStep(2)} className="transition-colors duration-150">
                {t("invoices.notes.continue", "Continue")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-foreground">
              {t("invoices.notes.debit.step2Title", "Debit details")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="debit-amount">
                {t("invoices.notes.debit.amountLabel", "Debit amount")} ({invoice.currency ?? "UYU"})
              </Label>
              <Input
                id="debit-amount"
                type="text"
                inputMode="decimal"
                value={debitAmount}
                onChange={(e) => setDebitAmount(e.target.value)}
                placeholder="0.00"
                className={amountError ? "border-destructive" : ""}
              />
              {amountError && (
                <p className="text-sm text-destructive">{amountError}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="debit-reason">{t("invoices.notes.debit.reasonOptional", "Reason or description (optional)")}</Label>
              <Input
                id="debit-reason"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder={t("invoices.notes.debit.reasonPlaceholder", "e.g. Additional charges")}
              />
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setStep(1)} className="transition-colors duration-150">
                {t("pages.common.back", "Back")}
              </Button>
              <Button onClick={() => setStep(3)} disabled={!canProceedStep2} className="transition-colors duration-150">
                {t("invoices.notes.continue", "Continue")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {step === 3 && (
        <>
          <AccountingPreview
            currency={invoice.currency ?? "UYU"}
            originalTotal={originalTotal}
            totalCreditedSoFar={totalCreditedSoFar}
            thisAmount={amountNum}
            newRemainingBalance={newRemaining}
            noteType="debit"
          />
          <Card>
            <CardContent className="flex justify-end gap-2 pt-6">
              <Button variant="outline" onClick={() => setStep(2)} className="transition-colors duration-150">
                {t("pages.common.back", "Back")}
              </Button>
              <Button onClick={() => setStep(4)} className="transition-colors duration-150">
                {t("invoices.notes.continue", "Continue")}
              </Button>
            </CardContent>
          </Card>
        </>
      )}

      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-foreground">
              {t("invoices.notes.debit.step4Title", "Confirmation")}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {t("invoices.notes.debit.step4Warning", "This document will be sent to DGI and cannot be edited or deleted.")}
            </p>
            <div className="flex items-start gap-2">
              <Checkbox
                id="debit-confirm"
                checked={confirmed}
                onCheckedChange={(c) => setConfirmed(c === true)}
              />
              <Label htmlFor="debit-confirm" className="text-sm font-normal cursor-pointer">
                {t("invoices.notes.debit.confirmCheckbox", "I understand and wish to issue this Debit Note.")}
              </Label>
            </div>
            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setStep(3)} className="transition-colors duration-150">
                {t("pages.common.back", "Back")}
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!canSubmitStep4 || createMutation.isPending}
                className="transition-colors duration-150"
              >
                {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                {t("invoices.notes.debit.submit", "Issue Debit Note")}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
